'use client';

import Link from 'next/link';
import { type RefCallback, useEffect, useState } from 'react';
import { MapPinIcon } from '@heroicons/react/24/outline';
import { normalizeRoomId } from '@/app/lib/rooms/normalize';
import { EntityPreviewTrigger } from '@/app/Components/EntityPreview';

export interface RoomPreview {
  id: string;
  href: string;
  buildingName: string | null;
  campus: string | null;
  roomStyle: string | null;
  capacity: number | null;
  panoramaImageUrl: string | null;
  seating: string[];
  writingSurfaces: string[];
  elevator: string | null;
  system: string | null;
}

export function RoomLocationPreviewCard({ roomId, preview }: { roomId: string; preview: RoomPreview | null | undefined }) {
  return (
    <>
      {preview?.panoramaImageUrl ? (
        <span className="block h-24 overflow-hidden bg-[var(--ss-surface-subtle)]">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={preview.panoramaImageUrl} alt="" className="h-full w-full object-cover" />
        </span>
      ) : null}
      <span className="block p-4">
        <span className="block text-sm font-semibold">{preview?.id ?? roomId}</span>
        {preview === undefined ? <span className="mt-2 block text-sm text-[var(--ss-text-muted)]">Loading classroom details…</span> : preview ? (
          <>
            <span className="mt-1 block text-sm text-[var(--ss-text-muted)]">{[preview.buildingName, preview.campus].filter(Boolean).join(' · ')}</span>
            <span className="mt-3 grid grid-cols-2 gap-2 text-xs">
              <span><strong className="block text-[var(--ss-text)]">Capacity</strong>{preview.capacity ? `${preview.capacity} seats` : 'Not listed'}</span>
              <span><strong className="block text-[var(--ss-text)]">Room</strong>{preview.roomStyle ?? 'Not listed'}</span>
              <span><strong className="block text-[var(--ss-text)]">Seating</strong>{preview.seating.join(', ') || 'Not listed'}</span>
              <span><strong className="block text-[var(--ss-text)]">System</strong>{preview.system ?? 'Not listed'}</span>
            </span>
            <span className="mt-3 block text-xs font-semibold text-[var(--ss-brand)]">Open full room guide</span>
          </>
        ) : <span className="mt-2 block text-sm text-[var(--ss-text-muted)]">No DCS classroom profile is available for this location.</span>}
      </span>
    </>
  );
}

const previewCache = new Map<string, RoomPreview | null>();

export default function RoomLocationLink({ location, className = '' }: { location: string; className?: string }) {
  const roomId = normalizeRoomId(location);
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState<RoomPreview | null | undefined>(() => roomId ? previewCache.get(roomId) : null);

  useEffect(() => {
    if (!open || !roomId || previewCache.has(roomId)) return;
    let active = true;
    fetch(`/api/rooms/${encodeURIComponent(roomId)}`)
      .then(async (response) => response.ok ? await response.json() as RoomPreview : null)
      .then((value) => {
        previewCache.set(roomId, value);
        if (active) setPreview(value);
      })
      .catch(() => {
        previewCache.set(roomId, null);
        if (active) setPreview(null);
      });
    return () => { active = false; };
  }, [open, preview, roomId]);

  if (!roomId) return <span className={className}>{location}</span>;

  return (
    <EntityPreviewTrigger
      label={`${roomId} room preview`}
      width={352}
      estimatedHeight={250}
      onOpenChange={setOpen}
      preview={<span data-room-preview={roomId}><RoomLocationPreviewCard roomId={roomId} preview={preview} /></span>}
    >
      {(triggerProps) => (
        <Link
          ref={triggerProps.ref as RefCallback<HTMLAnchorElement>}
          href={`/rooms/${encodeURIComponent(roomId)}`}
          aria-describedby={triggerProps['aria-describedby']}
          onMouseEnter={triggerProps.onMouseEnter}
          onMouseLeave={triggerProps.onMouseLeave}
          onFocus={triggerProps.onFocus}
          onBlur={triggerProps.onBlur}
          className={`inline-flex items-center gap-1 rounded-sm underline decoration-[var(--ss-border-strong)] underline-offset-4 transition-colors hover:text-[var(--ss-brand)] hover:decoration-[var(--ss-brand)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)] motion-reduce:transition-none ${className}`}
        >
          <MapPinIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {location}
        </Link>
      )}
    </EntityPreviewTrigger>
  );
}
